<template>
  <div class="page-container">
    <md-app
      md-waterfall
      md-mode="fixed">
      <md-app-toolbar>
        <div class="md-toolbar-row">
          <ScenarioHeader
            :scenario-id="parseInt($route.params.scenarioId)"
            :scenario-state="scenarioData.scenario_state"/>
        </div>
      </md-app-toolbar>
      <md-app-content>
        <form
          @submit.prevent="updateScenario()"
          class="scenario__form">
          <div
            class="alert alert-danger scenario-update-modal mb-20"
            v-if='scenarioResultsValids'>
            <div class="md-title">Modification du scénario</div>
            <div class="alert__content">
              <p class="md-body-2">Attention, toute modification dans cet écran va écraser les résultats actuels et annuler les modifications saisies dans
                <router-link
                  class="md-accent-link"
                  :to="{ name: 'Scenario refine', params: { scenarioId: $route.params.scenarioId }}"><strong>"Affinage"</strong></router-link>.
              </p>
              <p class="md-body-2">Afin de conserver vos modifications, vous pouvez dupliquer le scénario actuel depuis la section
                <router-link
                  class="md-accent-link"
                  :to="{ name: 'Scenarios'}"><strong>"Scénarios"</strong></router-link>.
              </p>
            </div>
          </div>
          <div class="grid">
            <fieldset>
              <legend>Années de calcul</legend>
              <div class="md-layout md-gutter">
                <div class="md-layout-item ">
                  <md-field
                    :class="{ 'md-invalid': $v.scenarioYear.$error || fieldsErrors.year }">
                    <label for="scenarioYear">Année de départ</label>
                    <md-select
                      v-model.trim.lazy="$v.scenarioYear.$model"
                      name="scenarioYear">
                      <md-option
                        v-for="scenarioYear in scenarioYears"
                        :key='`scenario-year-${scenarioYear}`'
                        :value="scenarioYear">{{ scenarioYear }}</md-option>
                    </md-select>
                    <span
                      class="md-error"
                      v-if="!fieldsErrors.year && !$v.scenarioYear.between">L'année doit être comprise entre {{ $v.scenarioYear.$params.between.min }} et {{ $v.scenarioYear.$params.between.max }}</span>
                    <span
                      class="md-error"
                      v-if="!fieldsErrors.year && !$v.scenarioYear.required">Ce champ est obligatoire</span>
                    <span
                      class="md-error"
                      v-if="fieldsErrors.year">{{ fieldsErrors.year }}</span>
                  </md-field>
                </div>
                <div class="md-layout-item">
                  <md-field
                    :class="{ 'md-invalid': $v.scenarioReferenceYear.$error || fieldsErrors.reference_year }">
                    <label for="scenarioReferenceYear">Année de référence</label>
                    <md-select
                      v-model.trim.lazy="$v.scenarioReferenceYear.$model"
                      name="scenarioReferenceYear">
                      <md-option
                        v-for="scenarioReferenceYear in scenarioReferenceYears"
                        :key='`reference-year-${scenarioReferenceYear}`'
                        :value="scenarioReferenceYear">{{ scenarioReferenceYear }}</md-option>
                    </md-select>
                    <span
                      class="md-error"
                      v-if="!fieldsErrors.reference_year && !$v.scenarioReferenceYear.between">L'année doit être comprise entre {{ $v.scenarioReferenceYear.$params.between.min }} et {{ $v.scenarioReferenceYear.$params.between.max }}</span>
                    <span
                      class="md-error"
                      v-if="!fieldsErrors.reference_year && !$v.scenarioReferenceYear.required">Ce champ est obligatoire</span>
                    <span
                      class="md-error"
                      v-if="fieldsErrors.reference_year">{{ fieldsErrors.reference_year }}</span>
                  </md-field>
                </div>
              </div>
            </fieldset>
            <fieldset>
              <legend>Investissement annuel</legend>
              <YearlyValuesField
                :unit="'M €'"
                :field-title="'Investissement annuel'"
                :years-cluser-list="scenarioYearList"
                :initial-values-list="scenarioInvestments"
                :min-year="scenarioYear"
                :max-year="scenarioMaxYear"
                @validate:valuesList="scenarioInvestments = $event" />
            </fieldset>
            <fieldset>
              <legend>Lieux atypiques et PPI</legend>
              <div>
                <md-checkbox
                  v-model="scenarioIncludeAtypicalPlaces"
                  class="md-primary">Prendre en compte des lieux atypiques</md-checkbox>
                <md-checkbox
                  v-model="scenarioIncludePPI"
                  class="md-primary">Prendre en compte la PPI</md-checkbox>
                <div
                  v-if="scenarioIncludePPI"
                  class="md-layout md-gutter"
                  style="margin-top: 1rem;">
                  <div class="md-layout-item">
                    <md-field>
                      <label for="scenarioPPICollection">Liste PPI à utiliser</label>
                      <md-select
                        v-model="scenarioPPICollection"
                        name="scenarioPPICollection"
                        id="scenarioPPICollection">
                        <md-option
                          v-for="collection in ppiCollections"
                          :key="`ppi-collection-${collection.id}`"
                          :value="collection.id">{{ collection.name }}</md-option>
                      </md-select>
                    </md-field>
                  </div>
                </div>
              </div>
            </fieldset>
            <div>
              <fieldset ref="energyCO2ContentFieldset">
                <legend>Évolution des contenus CO2 et % EnR (hors RC&F)</legend>
                <div>
                  <label class="select-title">Source de données :</label>
                  <md-radio
                    v-model="energyCO2ContentRnOrigin"
                    value="provided"
                    class="md-primary">Utiliser les valeurs de prospective contenu GES et parts EnR PIMO (hors RC&F)</md-radio>
                  <md-radio
                    v-model="energyCO2ContentRnOrigin"
                    value="imported"
                    class="md-primary">Importer un scénario d'évolution des contenus GES et des parts EnR</md-radio>
                </div>

                <div
                  v-if="energyCO2ContentRnOrigin == 'imported'"
                  class="m-20">
                  <label class="select-title">Importer un nouveau fichier :</label>
                  <md-field>
                    <label>Fichier d'entrée</label>
                    <md-file
                      accept=".xlsx, .xlsm"
                      placeholder="Importer un nouveau fichier"
                      v-model="energyCO2ContentRnFileImport.input_file_name"
                      @click="resetInputFile('energyMDFile')"
                      @md-change="energyFileChange"
                      ref="energyMDFile"
                    />
                    <md-button
                      class="md-raised md-primary float-right"
                      @click="importEnergyFile"
                      :disabled="energyCO2ContentRnFileImport.buttonDisabled">
                      Importer le fichier
                    </md-button>
                  </md-field>
                  <div
                    class="alert alert-danger mt-2"
                    v-if='energyCO2ContentRnFileErrors'>
                    Le dernier fichier utilisé pour l'import de données (le {{ energyCO2ContentRnFileErrorsCreationDate }}) contient des erreurs.
                    Vous devez les corriger et importer de nouveau les données afin de pouvoir utiliser votre propre scénario d'évolution des contenus GES et des parts EnR. <a
                      class="md-accent-link"
                      :href="energyCO2ContentRnFileErrors"
                      target="_blank">Visualiser les erreurs dans le fichier</a>
                  </div>
                  <div
                    class="alert alert-info mt-2"
                    v-else-if="energyCO2ContentRnFile">
                    Le scénario sera calculé en utilisant les valeurs importées le {{ energyCO2ContentRnFileCreationDate }}. <a
                      class="md-accent-link"
                      :href="energyCO2ContentRnFile"
                      target="_blank">Consulter les valeurs importées</a>
                  </div>
                </div>
              </fieldset>

              <fieldset ref="heatingNetworkCO2ContentFieldset">
                <legend>Contenus CO2 et % EnR des RC&F</legend>
                <div>
                  <label class="select-title">Source de données :</label>
                  <md-radio
                    v-model="heatingNetworkCO2ContentRnOrigin"
                    value="provided"
                    class="md-primary">Garder les contenu GES et parts EnR des RC&F constants</md-radio>
                  <md-radio
                    v-model="heatingNetworkCO2ContentRnOrigin"
                    value="imported"
                    class="md-primary">Importer un scénario d'évolution des contenus GES et des parts EnR des RC&F locaux</md-radio>
                </div>
                <div
                  v-if="heatingNetworkCO2ContentRnOrigin == 'imported'"
                  class="m-20">
                  <label class="select-title">Importer un nouveau fichier :</label>
                  <md-field>
                    <label>Fichier d'entrée</label>
                    <md-file
                      accept=".xlsx, .xlsm"
                      placeholder="Importer un nouveau fichier"
                      v-model="heatingNetworkCO2ContentRnFileImport.input_file_name"
                      @click="resetInputFile('heatingNetworkMDFile')"
                      @md-change="heatingNetworkFileChange"
                      ref="heatingNetworkMDFile"
                    />
                    <md-button
                      class="md-raised md-primary float-right"
                      @click="importHeatingNetworkFile"
                      :disabled="heatingNetworkCO2ContentRnFileImport.buttonDisabled">
                      Importer le fichier
                    </md-button>
                  </md-field>
                  <div
                    class="alert alert-danger mt-2"
                    v-if='heatingNetworkCO2ContentRnFileErrors'>
                    Le dernier fichier utilisé pour l'import de données (le {{ heatingNetworkCO2ContentRnFileErrorsCreationDate }}) contient des erreurs.
                    Vous devez les corriger et importer de nouveau les données afin de pouvoir utiliser votre propre scénario d'évolution des contenus GES et des parts EnR des RC&F locaux. <a
                      class="md-accent-link"
                      :href="heatingNetworkCO2ContentRnFileErrors"
                      target="_blank">Visualiser les erreurs dans le fichier</a>
                  </div>
                  <div
                    class="alert alert-info mt-2"
                    v-else-if="heatingNetworkCO2ContentRnFile">
                    Le scénario sera calculé en utilisant les valeurs importées le {{ heatingNetworkCO2ContentRnFileCreationDate }}. <a
                      class="md-accent-link"
                      :href="heatingNetworkCO2ContentRnFile"
                      target="_blank">Consulter les valeurs importées</a>
                  </div>
                </div>
              </fieldset>

              <fieldset ref="notificationFieldset">
                <legend>Notifications</legend>
                <div>
                  <label class="select-title">Notification par email :</label>
                  <md-checkbox v-model="scenarioNotifyUserByEmail">Recevoir un email à l'issue de la génération</md-checkbox>
                </div>
              </fieldset>
            </div>

            <fieldset>
              <legend>Coût des énergies</legend>
              <md-table>
                <md-table-row>
                  <md-table-head>Énergie</md-table-head>
                  <md-table-head>Coût</md-table-head>
                </md-table-row>

                <md-table-row
                  v-for="(scenarioEnergyVariation, i) in scenarioEnergyVariations"
                  :key="`energy-variation-${i}`">
                  <md-table-cell>{{ energyList[i].label }}</md-table-cell>
                  <md-table-cell>
                    <YearlyValuesField
                      :unit="'%'"
                      :dialog-title="`Coût des énergies :  ${energyList[i].label}`"
                      :years-cluser-list="scenarioYearList"
                      :initial-values-list="scenarioEnergyVariation"
                      :min-year="scenarioYear"
                      :max-year="scenarioMaxYear"
                      allow-negative-value
                      @validate:valuesList="updateScenarioEnergyVariation(i, $event)" />
                  </md-table-cell>
                </md-table-row>
              </md-table>
            </fieldset>
            <fieldset>
              <legend>Changements d'énergies</legend>
              <md-checkbox
                v-model="scenarioActivateEnergySwitch"
                class="md-primary">Activer le choix automatique des changements d'énergies</md-checkbox>

              <template v-if="scenarioActivateEnergySwitch">
                <div class="alert alert-info mb-20">
                  Les pourcentages renseignés ici vont s'appliquer pour attribuer les changements d'énergies restants à effectuer après que les <strong>"Changements d'énergies par lieux"</strong> saisis à la page suivante aient été pris en compte.
                </div>
                <md-table>
                  <md-table-row>
                    <md-table-head>Énergie</md-table-head>
                    <md-table-head>Pourcentage</md-table-head>
                  </md-table-row>

                  <md-table-row
                    v-for="switchPercentage in energySwitchPercentages"
                    :key="`energy-switch-percentage-${switchPercentage.energy}`">
                    <md-table-cell>{{ switchPercentage.energy_display }}</md-table-cell>
                    <md-table-cell>
                      <md-field
                        :class="{ 'md-invalid': switchPercentageInvalid(switchPercentage) }">
                        <md-input
                          @focus="$event.target.select()"
                          v-model.number="switchPercentage.percentage"
                          class="small-input"
                          type="number"
                          step="0.5"
                          min="0"
                          required />
                        <span class="md-suffix">&nbsp;%</span>
                        <span
                          class="md-error"
                          v-if="switchPercentage.percentage === ''">Ce champ est obligatoire</span>
                        <span
                          class="md-error"
                          v-else-if="switchPercentage.percentage < 0">La valeur doit être positive</span>
                      </md-field>
                    </md-table-cell>
                  </md-table-row>
                  <md-table-row>
                    <md-table-cell><strong>Total</strong></md-table-cell>
                    <md-table-cell class="pb-10">
                      <md-field :class="{ 'md-invalid': energySwitchPercentagesTotalInvalid }">
                        <md-input
                          :value="energySwitchPercentagesTotal"
                          :readonly="true"
                          class="small-input"
                          type="number"
                          step="0.5"
                          min="0"
                          required />
                        <span class="md-suffix">&nbsp;%</span>
                        <span
                          class="md-error"
                          v-if="energySwitchPercentagesTotal > 100">La somme des pourcentages de peut pas dépasser 100%</span>
                      </md-field>
                    </md-table-cell>
                  </md-table-row>
                </md-table>
              </template>
            </fieldset>
          </div>
          <!-- <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-25">
              <YearlyValuesField
                :unit="'%'"
                :field-title="'Augmentation du parc'"
                :years-cluser-list="scenarioYearList"
                :initial-values-list="scenarioPopulationVariations"
                :min-year="scenarioYear"
                :max-year="scenarioMaxYear"
                @validate:valuesList="scenarioPopulationVariations = $event" />
            </div>
          </div> -->
        </form>
        <md-button
          @click="saveButtonClicked(true)"
          class="md-raised md-primary float-right">
          Enregistrer et configurer le script
        </md-button>
        <md-button
          @click="saveButtonClicked(false)"
          class="md-raised md-primary float-right">
          Enregistrer
        </md-button>

        <md-dialog-confirm
          :md-active.sync="confirmUpdateModal.showModal"
          md-title="Confirmer les modifications ?"
          md-content="<strong>Attention</strong>, cette action va écraser les résultats actuels et annuler les modifications saisies dans <strong>&#34;Affinage&#34;</strong>"
          md-confirm-text="Continuer"
          md-cancel-text="Annuler"
          @md-cancel="confirmUpdateModal.showModal=false"
          @md-confirm="save(confirmUpdateModal.continueToScript)" />

        <md-dialog-alert
          :md-active.sync="cO2ContentRnErrorModal.showModal"
          md-title="Paramétrage contenus CO2 et % EnR invalide"
          :md-content="cO2ContentRnErrorModal.errorMessage"
        />
      </md-app-content>
    </md-app>
  </div>
</template>

<script>
import session from '../api/session'
import ScenarioHeader from '../components/ScenarioHeader.vue'
import YearlyValuesField from '../components/YearlyValuesField.vue'
import { validationMixin } from 'vuelidate'
import { required, between } from 'vuelidate/lib/validators'

export default {
  name: 'ScenarioSettings',
  mixins: [validationMixin],
  components: {
    ScenarioHeader,
    YearlyValuesField
  },
  data () {
    return {
      fieldsErrors: {},
      energyList: [],
      scenarioData: {scenario_state: 'new'},
      scenarioNotifyUserByEmail: false,
      scenarioYears: [],
      scenarioYear: new Date().getFullYear(),
      scenarioReferenceYears: [],
      scenarioReferenceYear: new Date().getFullYear(),
      scenarioMinYear: new Date().getFullYear(),
      scenarioMaxYear: 2050,
      placeEnergyYearMinYear: new Date().getFullYear(),
      placeEnergyYearMaxYear: new Date().getFullYear(),

      energyCO2ContentRnOrigin: null,
      energyCO2ContentRnFile: null,
      energyCO2ContentRnFileCreationDate: null,
      energyCO2ContentRnFileErrors: null,
      energyCO2ContentRnFileErrorsCreationDate: null,
      energyCO2ContentRnFileImport: {
        input_file_name: null,
        fileContent: null,
        buttonDisabled: true
      },

      heatingNetworkCO2ContentRnOrigin: null,
      heatingNetworkCO2ContentRnFile: null,
      heatingNetworkCO2ContentRnFileContent: null,
      heatingNetworkCO2ContentRnFileCreationDate: null,
      heatingNetworkCO2ContentRnFileErrors: null,
      heatingNetworkCO2ContentRnFileErrorsCreationDate: null,
      heatingNetworkCO2ContentRnFileImport: {
        input_file_name: null,
        fileContent: null,
        buttonDisabled: true
      },

      cO2ContentRnErrorModal: {
        showModal: false,
        errorMessage: ''
      },

      scenarioIncludeAtypicalPlaces: null,
      scenarioIncludePPI: null,
      scenarioPPICollection: null,
      ppiCollections: [],
      scenarioInvestments: [],
      // scenarioPopulationVariations: [],
      scenarioEnergyVariations: [],
      scenarioActivateEnergySwitch: null,
      energySwitchPercentages: [],
      confirmUpdateModal: {
        showModal: false,
        continueToScript: false
      }
    }
  },
  validations () {
    const validations = {
      scenarioYear: {
        required,
        between: between(this.scenarioMinYear, this.scenarioMaxYear)
      },
      scenarioReferenceYear: {
        required,
        between: between(this.placeEnergyYearMinYear, this.placeEnergyYearMaxYear)
      }
    }
    return validations
  },
  mounted () {
    this.$store.dispatch('routing/update_title', 'Réglages')
    this.$store.dispatch('routing/update_return_kwargs', null)
    this.loadApi(this.$route.params.scenarioId)
  },
  computed: {
    scenarioYearList () {
      const nbPerYear = 10
      let nb = 0
      let yearList = []
      let yearCluster = []

      for (let year = this.scenarioYear; year <= this.scenarioData.scenario_end_year; year++) {
        nb++
        if ((nb % nbPerYear) === 0) {
          yearCluster.push(year)
          yearList.push(yearCluster)
          yearCluster = []
        } else if ((nb % nbPerYear) === 1) {
          yearCluster.push(year)
        }
      }

      if (yearCluster.length > 0) {
        yearCluster.push(this.scenarioData.scenario_end_year)
        yearList.push(yearCluster)
      }
      return yearList
    },
    scenarioResultsValids () {
      return ['refine', 'results'].includes(this.scenarioData.scenario_state)
    },
    energyCO2ContentRnError () {
      return this.energyCO2ContentRnOrigin === 'imported' && this.energyCO2ContentRnFileErrors
    },
    heatingNetworkCO2ContentRnError () {
      return this.heatingNetworkCO2ContentRnOrigin === 'imported' && this.heatingNetworkCO2ContentRnFileErrors
    },
    energySwitchPercentagesTotal () {
      let total = 0
      this.energySwitchPercentages.forEach(switchPercentage => {
        if (switchPercentage.percentage) {
          total = total + switchPercentage.percentage
        }
      })
      return total
    },
    energySwitchPercentagesTotalInvalid () {
      return this.energySwitchPercentagesTotal > 100
    }

  },
  watch: {
    scenarioIncludePPI: function (newValue) {
      // When PPI is enabled and no collection is selected, select the main collection
      if (newValue === true && !this.scenarioPPICollection && this.ppiCollections.length > 0) {
        var mainCollection = this.ppiCollections.find(function (collection) {
          return collection.name === 'Liste principale'
        })
        if (mainCollection) {
          this.scenarioPPICollection = mainCollection.id
        }
      }
    }
  },
  methods: {
    updateScenarioEnergyVariation (i, data) {
      this.scenarioEnergyVariations[i] = data
    },
    saveButtonClicked (continueToScript) {
      if (this.energyCO2ContentRnError) {
        this.cO2ContentRnErrorModal.showModal = true
        this.cO2ContentRnErrorModal.errorMessage = "Le calcul du scénario ne peut pas être effectué car votre paramétrage de <strong>l'évolution des contenus CO2 et % EnR (hors RC&F)</strong> est invalide. <br>Veuillez corriger les erreurs dans votre fichier d'import, ou utiliser les valeurs par défaut."

        const energyCO2ContentFieldset = this.$refs.energyCO2ContentFieldset
        const top = energyCO2ContentFieldset.offsetTop
        document.querySelector('.md-app-scroller').scroll({top: top, behavior: 'smooth'})
      } else if (this.heatingNetworkCO2ContentRnError) {
        this.cO2ContentRnErrorModal.showModal = true
        this.cO2ContentRnErrorModal.errorMessage = "Le calcul du scénario ne peut pas être effectué car votre paramétrage de <strong>des contenus CO2 et % EnR des RC&F</strong> est invalide. <br>Veuillez corriger les erreurs dans votre fichier d'import, ou utiliser les valeurs par défaut."
        const heatingNetworkCO2ContentFieldset = this.$refs.heatingNetworkCO2ContentFieldset
        const top = heatingNetworkCO2ContentFieldset.offsetTop
        document.querySelector('.md-app-scroller').scroll({top: top, behavior: 'smooth'})
      } else if (this.scenarioResultsValids) {
        this.confirmUpdateModal.continueToScript = continueToScript
        this.confirmUpdateModal.showModal = true
      } else {
        this.save(continueToScript)
      }
    },
    save (continueToScript) {
      if (continueToScript) {
        this.putScenarioData(this.routeToScript)
      } else {
        this.putScenarioData()
      }
    },
    routeToScript () {
      this.$router.push({name: 'Scenario script', params: {scenarioId: this.$route.params.scenarioId}})
    },
    async loadApi (scenarioId) {
      this.$store.dispatch('loading/begin_loading')
      const scenarioDataResponse = await session.get(`/api/scenario/settings/${scenarioId}/`)
      const energyResponse = await session.get(`/api/parameters/energies/`)
      const ppiCollectionsResponse = await session.get('/api/userdata/ppi-collections/')

      this.scenarioData = scenarioDataResponse.data
      this.ppiCollections = ppiCollectionsResponse.data || []

      if (this.scenarioData.generating) {
        this.$router.push({name: 'Scenarios list'})
      }

      this.energyList = energyResponse.data

      this.$store.dispatch('routing/update_title', this.scenarioData.label)

      // Init scenario notifications
      this.scenarioNotifyUserByEmail = this.scenarioData.notify_user_by_email

      // Init scenario year
      this.scenarioYear = this.scenarioData.year
      this.scenarioReferenceYear = this.scenarioData.reference_year
      this.placeEnergyYearMinYear = this.scenarioData.place_energy_year_min_year
      this.placeEnergyYearMaxYear = this.scenarioData.place_energy_year_max_year

      // Caution: This will prevent some scenarios from running properly without changin their start year
      //  this.scenarioMinYear =  this.scenarioData.place_energy_year_max_year + 1
      this.scenarioMinYear = this.scenarioData.place_energy_year_max_year
      this.scenarioMaxYear = this.scenarioData.scenario_end_year
      let nbYears = this.scenarioMaxYear - this.scenarioMinYear + 1
      this.scenarioYears = Array.from(new Array(nbYears), (val, index) => index + this.scenarioMinYear)

      nbYears = this.placeEnergyYearMaxYear - this.placeEnergyYearMinYear + 1
      this.scenarioReferenceYears = Array.from(new Array(nbYears), (val, index) => index + this.placeEnergyYearMinYear)

      // Init scenario CO2 content and RN % values
      this.energyCO2ContentRnOrigin = this.scenarioData.energy_co2_content_rn_origin
      this.energyCO2ContentRnFile = this.scenarioData.energy_co2_content_rn_file
      this.energyCO2ContentRnFileCreationDate = this.scenarioData.energy_co2_content_rn_file_creation_date
      this.energyCO2ContentRnFileErrors = this.scenarioData.energy_co2_content_rn_file_errors
      this.energyCO2ContentRnFileErrorsCreationDate = this.scenarioData.energy_co2_content_rn_file_errors_creation_date

      this.heatingNetworkCO2ContentRnOrigin = this.scenarioData.heating_network_co2_content_rn_origin
      this.heatingNetworkCO2ContentRnFile = this.scenarioData.heating_network_co2_content_rn_file
      this.heatingNetworkCO2ContentRnFileCreationDate = this.scenarioData.heating_network_co2_content_rn_file_creation_date
      this.heatingNetworkCO2ContentRnFileErrors = this.scenarioData.heating_network_co2_content_rn_file_errors
      this.heatingNetworkCO2ContentRnFileErrorsCreationDate = this.scenarioData.heating_network_co2_content_rn_file_errors_creation_date

      // Init scenario booleans
      this.scenarioIncludeAtypicalPlaces = this.scenarioData.include_atypical_places
      this.scenarioIncludePPI = this.scenarioData.include_ppi
      this.scenarioPPICollection = this.scenarioData.ppi_collection || null

      // If PPI is enabled but no collection is selected, select the main collection by default
      if (this.scenarioIncludePPI && !this.scenarioPPICollection && this.ppiCollections.length > 0) {
        var mainCollection = this.ppiCollections.find(function (collection) {
          return collection.name === 'Liste principale'
        })
        if (mainCollection) {
          this.scenarioPPICollection = mainCollection.id
        }
      }

      this.scenarioActivateEnergySwitch = this.scenarioData.activate_energy_switch

      // Init scenario investments
      this.scenarioInvestments = []
      for (let year = this.scenarioMinYear; year <= this.scenarioMaxYear; year++) {
        let scenarioInvestment = this.scenarioData['scenario_investments'][this.scenarioData['scenario_investments'].findIndex((i) => { return i.year === year })]

        if (scenarioInvestment) {
          this.scenarioInvestments.push({ 'year': year, 'value': scenarioInvestment.max_investment / 1000000 })
        } else {
          this.scenarioInvestments.push({ 'year': year, 'value': null })
        }
      }

      // Init scenario population variations
      // this.scenarioPopulationVariations = []
      // for (year = this.scenarioMinYear; year <= this.scenarioMaxYear; year++) {
      //   let scenarioPopulationVariation = this.scenarioData['scenario_population_variations'][this.scenarioData['scenario_population_variations'].findIndex((i) => { return i.year === year })]

      //   if (scenarioPopulationVariation) {
      //     this.scenarioPopulationVariations.push({ 'year': year, 'value': Number((scenarioPopulationVariation.population_variation * 100).toFixed(2)) })
      //   } else {
      //     this.scenarioPopulationVariations.push({ 'year': year, 'value': null })
      //   }
      // }

      // Init scenario energy variation
      this.scenarioEnergyVariations = []

      for (let i in this.energyList) {
        let energy = this.energyList[i]
        let energyId = energy.id
        this.scenarioEnergyVariations.push([])

        for (let year = this.scenarioMinYear; year <= this.scenarioMaxYear; year++) {
          let scenarioEnergyVariation = this.scenarioData['scenario_energy_variations'][this.scenarioData['scenario_energy_variations'].findIndex((i) => { return i.year === year && i.energy === energyId })]

          if (scenarioEnergyVariation) {
            this.scenarioEnergyVariations[i].push({ 'year': year, 'value': Number((scenarioEnergyVariation.energy_price_variation * 100).toFixed(2)) })
          } else {
            this.scenarioEnergyVariations[i].push({ 'year': year, 'value': null })
          }
        }
      }

      // Init energy switch percentages
      this.energySwitchPercentages = []
      this.scenarioData['energy_switch_percentages'].forEach(switchPercentage => {
        this.energySwitchPercentages.push(
          {
            'energy': switchPercentage.energy,
            'energy_display': switchPercentage.energy_display,
            'percentage': parseFloat(switchPercentage.percentage)
          })
      })

      this.$store.dispatch('loading/end_loading')
    },
    async putScenarioData (callback) {
      let scenarioId = this.$route.params.scenarioId

      // Update notifications
      this.scenarioData.notify_user_by_email = this.scenarioNotifyUserByEmail

      // Update year & reference year
      this.scenarioData.year = this.scenarioYear
      this.scenarioData.reference_year = this.scenarioReferenceYear

      // Update CO2 content and RN % values
      this.scenarioData.energy_co2_content_rn_origin = this.energyCO2ContentRnOrigin
      this.scenarioData.heating_network_co2_content_rn_origin = this.heatingNetworkCO2ContentRnOrigin

      // Update booleans
      this.scenarioData.include_atypical_places = this.scenarioIncludeAtypicalPlaces
      this.scenarioData.include_ppi = this.scenarioIncludePPI
      this.scenarioData.ppi_collection = this.scenarioPPICollection || null
      this.scenarioData.activate_energy_switch = this.scenarioActivateEnergySwitch

      // Update investments
      let scenarioInvestments = []
      for (let year = this.scenarioYear; year <= this.scenarioData.scenario_end_year; year++) {
        let scenarioInvestment = this.scenarioInvestments[this.scenarioInvestments.findIndex((i) => { return i.year === year })]

        let maxInvestment = 0

        if (scenarioInvestment && scenarioInvestment.value) {
          maxInvestment = scenarioInvestment.value * 1000000
        }

        scenarioInvestments.push({'year': year, 'max_investment': maxInvestment})
      }
      this.scenarioData.scenario_investments = scenarioInvestments

      // Update population_variation
      // let scenarioPopulationVariations = []
      // for (year = this.scenarioYear; year <= this.scenarioData.scenario_end_year; year++) {
      //   let scenarioPopulationVariation = this.scenarioPopulationVariations[this.scenarioPopulationVariations.findIndex((i) => { return i.year === year })]

      //   let populationVariation = 0
      //   if (scenarioPopulationVariation && scenarioPopulationVariation.value) {
      //     populationVariation = parseFloat(scenarioPopulationVariation.value / 100).toFixed(4)
      //   }

      //   scenarioPopulationVariations.push({'year': year, 'population_variation': populationVariation})
      // }
      // this.scenarioData.scenario_population_variations = scenarioPopulationVariations

      // Update scenario energy variations
      let scenarioScenarioEnergyVariations = []

      for (let i in this.energyList) {
        let energy = this.energyList[i]
        let energyId = energy.id
        let energyScenarioScenarioEnergyVariations = this.scenarioEnergyVariations[i]

        for (let year = this.scenarioYear; year <= this.scenarioData.scenario_end_year; year++) {
          let scenarioScenarioEnergyVariation = energyScenarioScenarioEnergyVariations[energyScenarioScenarioEnergyVariations.findIndex((i) => { return i.year === year })]

          let energyPriceVariation = 0
          if (scenarioScenarioEnergyVariation && scenarioScenarioEnergyVariation.value) {
            energyPriceVariation = parseFloat(scenarioScenarioEnergyVariation.value / 100).toFixed(4)
          }

          scenarioScenarioEnergyVariations.push({'year': year, 'energy_price_variation': energyPriceVariation, 'energy': energyId})
        }
      }

      this.scenarioData.scenario_energy_variations = scenarioScenarioEnergyVariations

      this.scenarioData.energy_switch_percentages = []
      this.energySwitchPercentages.forEach(switchPercentage => {
        this.scenarioData.energy_switch_percentages.push({
          'energy': switchPercentage.energy,
          'percentage': switchPercentage.percentage
        })
      })

      this.$store.dispatch('loading/begin_loading')

      this.fieldsErrors = {}

      // The CO2 content and RN files must not be sent to backend, so we remove these fields from the post data
      const scenarioData = {...this.scenarioData,
        ...{
          energy_co2_content_rn_file: undefined,
          energy_co2_content_rn_file_creation_date: undefined,
          energy_co2_content_rn_file_errors: undefined,
          energy_co2_content_rn_file_errors_creation_date: undefined,
          heating_network_co2_content_rn_file: undefined,
          heating_network_co2_content_rn_file_creation_date: undefined,
          heating_network_co2_content_rn_file_errors: undefined,
          heating_network_co2_content_rn_file_errors_creation_date: undefined
        } }

      try {
        const response = await session.put(`/api/scenario/settings/${scenarioId}/`, scenarioData)
        this.scenarioData = response.data
        this.$store.dispatch('loading/end_loading')

        if (callback) {
          callback()
        }
      } catch (error) {
        const response = error.response
        if (response.status === 400) {
          for (const field in response.data) {
            let errorMessage = response.data[field][0]
            this.$set(this.fieldsErrors, field, errorMessage)
          }

          this.$store.commit('errors/SHOW', {'title': 'Formulaire invalide', 'content': 'Les données de votre formulaire sont invalides, vous devez corriger les erreurs pour pouvoir enregistrer vos paramètres.'})
        }
      }
    },
    resetInputFile (parentRef) {
      // Empty inner input file to ensure that file change event will be fired when the same file is set several times
      // https://stackoverflow.com/questions/54124977/vuejs-input-file-selection-event-not-firing-upon-selecting-the-same-file and https://stackoverflow.com/questions/49676654/vue-access-nested-childs-using-ref
      this.$refs[parentRef].$refs.inputFile.value = ''
    },
    energyFileChange (inputFilesContent) {
      if (inputFilesContent.length > 0) {
        this.energyCO2ContentRnFileImport.fileContent = inputFilesContent[0]
        this.energyCO2ContentRnFileImport.buttonDisabled = false
      } else {
        this.energyCO2ContentRnFileImport.fileContent = null
        this.energyCO2ContentRnFileImport.input_file_name = null
        this.energyCO2ContentRnFileImport.buttonDisabled = true
      }
    },
    heatingNetworkFileChange (inputFilesContent) {
      if (inputFilesContent.length > 0) {
        this.heatingNetworkCO2ContentRnFileImport.fileContent = inputFilesContent[0]
        this.heatingNetworkCO2ContentRnFileImport.buttonDisabled = false
      } else {
        this.heatingNetworkCO2ContentRnFileImport.fileContent = null
        this.heatingNetworkCO2ContentRnFileImport.input_file_name = null
        this.heatingNetworkCO2ContentRnFileImport.buttonDisabled = true
      }
    },
    async importEnergyFile () {
      let scenarioId = this.$route.params.scenarioId

      if (this.energyCO2ContentRnFileImport.fileContent) {
        this.$store.dispatch('loading/begin_loading')
        let formData = new FormData()
        formData.append('energy_co2_content_rn_file', this.energyCO2ContentRnFileImport.fileContent)

        const response = await session.post(`/api/scenario/settings/${scenarioId}/import-energy-co2-content-rn-file/`, formData, {headers: {'Content-Type': 'multipart/form-data'}})
        const responseData = response.data

        this.energyCO2ContentRnFile = responseData.energy_co2_content_rn_file
        this.energyCO2ContentRnFileCreationDate = responseData.energy_co2_content_rn_file_creation_date
        this.energyCO2ContentRnFileErrors = responseData.energy_co2_content_rn_file_errors
        this.energyCO2ContentRnFileErrorsCreationDate = responseData.energy_co2_content_rn_file_errors_creation_date

        this.energyCO2ContentRnFileImport.fileContent = null
        this.energyCO2ContentRnFileImport.input_file_name = null
        this.energyCO2ContentRnFileImport.buttonDisabled = true

        if (!this.energyCO2ContentRnFileErrors) {
          this.$store.commit('messages/SHOW', {
            label: 'Succès',
            description: 'Import réalisé avec succès',
            duration: 3000
          })
        } else {
          this.$store.commit('messages/SHOW', {
            label: 'Erreur',
            description: 'Des erreurs ont été rencontrées durant l\'import',
            duration: 3000
          })
        }

        this.$store.dispatch('loading/end_loading')
      }
    },

    async importHeatingNetworkFile () {
      let scenarioId = this.$route.params.scenarioId

      if (this.heatingNetworkCO2ContentRnFileImport.fileContent) {
        this.$store.dispatch('loading/begin_loading')

        let formData = new FormData()
        formData.append('heating_network_co2_content_rn_file', this.heatingNetworkCO2ContentRnFileImport.fileContent)

        const response = await session.post(`/api/scenario/settings/${scenarioId}/import-heating-network-co2-content-rn-file/`, formData, {headers: {'Content-Type': 'multipart/form-data'}})
        const responseData = response.data

        this.heatingNetworkCO2ContentRnFile = responseData.heating_network_co2_content_rn_file
        this.heatingNetworkCO2ContentRnFileCreationDate = responseData.heating_network_co2_content_rn_file_creation_date
        this.heatingNetworkCO2ContentRnFileErrors = responseData.heating_network_co2_content_rn_file_errors
        this.heatingNetworkCO2ContentRnFileErrorsCreationDate = responseData.heating_network_co2_content_rn_file_errors_creation_date

        this.heatingNetworkCO2ContentRnFileImport.fileContent = null
        this.heatingNetworkCO2ContentRnFileImport.input_file_name = null
        this.heatingNetworkCO2ContentRnFileImport.buttonDisabled = true

        if (!this.heatingNetworkCO2ContentRnFileErrors) {
          this.$store.commit('messages/SHOW', {
            label: 'Succès',
            description: 'Import réalisé avec succès',
            duration: 3000
          })
        } else {
          this.$store.commit('messages/SHOW', {
            label: 'Erreur',
            description: 'Des erreurs ont été rencontrées durant l\'import',
            duration: 3000
          })
        }

        this.$store.dispatch('loading/end_loading')
      }
    },

    resetScenarioData (callback) {
      this.loadApi(this.$route.params.scenarioId)
      if (callback) {
        callback()
      }
    },
    switchPercentageInvalid (switchPercentage) {
      return switchPercentage.percentage === '' || switchPercentage.percentage < 0
    }
  }
}
</script>

<style lang="scss" scoped>

.m-20 {
  margin-top: 20px;
}
.mb-20 {
  margin-bottom: 20px;
}
.mt-2{
  margin-top: 10px;
}
.pb-10{
  padding-bottom: 10px;
}
.md-accent-link{
  color: white !important;
}
.scenario__form {
  padding: 30px;
}
.first {
  max-width: 300px;
}
.select-title{
  display:block;
  font-weight: bold;
  margin-bottom: 10px;
}

fieldset{
  margin-bottom: 20px;
  border: 1px solid #ccc;
  legend {
    font-weight: bold;
    padding: 0 10px;
    color: #86ad19;
    font-size: 18px;
  }
}

.grid{
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap:10px;

  @media only screen and (min-width: 1400px){
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ScenarioSettings.vue